import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { CanvasBlock, DragInfo } from '../../forms/types';
import { FM_BUILDER_VIEWPORT_WIDTH } from '../../forms/formBuilder/constants';
import { FormBuilderCanvasBlock } from './FormBuilderCanvasBlock';

export interface FormBuilderCanvasProps {
  blocks: CanvasBlock[];
  selectedUid: string | null;
  onSelect: (uid: string | null) => void;
  viewport: keyof typeof FM_BUILDER_VIEWPORT_WIDTH;
  canvasWidth?: number;
  showGrid?: boolean;
  dragInfo: DragInfo;
  dropIndex: number | null;
  onBlockDragStart: (e: React.DragEvent, uid: string) => void;
  onBlockDragEnd: () => void;
  onDropZoneOver: (e: React.DragEvent, index: number) => void;
  onDropZoneLeave: () => void;
  onDropZoneDrop: (e: React.DragEvent, index: number) => void;
  onRemove: (uid: string) => void;
  onMove: (uid: string, dir: -1 | 1) => void;
  onDuplicate: (uid: string) => void;
}

export function FormBuilderCanvas({
  blocks,
  selectedUid,
  onSelect,
  viewport,
  canvasWidth,
  showGrid = true,
  dragInfo,
  dropIndex,
  onBlockDragStart,
  onBlockDragEnd,
  onDropZoneOver,
  onDropZoneLeave,
  onDropZoneDrop,
  onRemove,
  onMove,
  onDuplicate,
}: FormBuilderCanvasProps) {
  const isDragging = dragInfo !== null;
  const width = canvasWidth ?? FM_BUILDER_VIEWPORT_WIDTH[viewport];

  return (
    <main
      className={cn('fm-builder-canvas cal-scroll min-h-0 flex-1 overflow-y-auto', showGrid && 'fm-builder-canvas-grid')}
      onClick={() => {
        onSelect(null);
      }}
    >
      <div
        className="mx-auto transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ width, maxWidth: '100%' }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="fm-builder-canvas-sheet">
          {blocks.length === 0 ? (
            <DropZone
              index={0}
              active={dropIndex === 0}
              dragging={isDragging}
              empty
              onDragOver={(e) => {
                onDropZoneOver(e, 0);
              }}
              onDragLeave={onDropZoneLeave}
              onDrop={(e) => {
                onDropZoneDrop(e, 0);
              }}
            />
          ) : (
            <>
              <DropZone
                index={0}
                active={dropIndex === 0}
                dragging={isDragging}
                onDragOver={(e) => {
                  onDropZoneOver(e, 0);
                }}
                onDragLeave={onDropZoneLeave}
                onDrop={(e) => {
                  onDropZoneDrop(e, 0);
                }}
              />
              {blocks.map((block, idx) => (
                <div key={block.uid}>
                  <div
                    className={cn(isDragging && 'fm-builder-block-drop-target')}
                    onDragOver={(e) => {
                      if (!isDragging) return;
                      e.preventDefault();
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const insertIndex = e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
                      onDropZoneOver(e, insertIndex);
                    }}
                    onDrop={(e) => {
                      if (!isDragging) return;
                      e.preventDefault();
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const insertIndex = e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
                      onDropZoneDrop(e, insertIndex);
                    }}
                  >
                    <FormBuilderCanvasBlock
                      block={block}
                      index={idx}
                      total={blocks.length}
                      selected={selectedUid === block.uid}
                      isBeingDragged={dragInfo?.type === 'reorder' && dragInfo.uid === block.uid}
                      onSelect={() => {
                        onSelect(block.uid);
                      }}
                      onRemove={() => {
                        onRemove(block.uid);
                      }}
                      onMove={(dir) => {
                        onMove(block.uid, dir);
                      }}
                      onDuplicate={() => {
                        onDuplicate(block.uid);
                      }}
                      onBlockDragStart={(e) => {
                        onBlockDragStart(e, block.uid);
                      }}
                      onBlockDragEnd={onBlockDragEnd}
                    />
                  </div>
                  <DropZone
                    index={idx + 1}
                    active={dropIndex === idx + 1}
                    dragging={isDragging}
                    onDragOver={(e) => {
                      onDropZoneOver(e, idx + 1);
                    }}
                    onDragLeave={onDropZoneLeave}
                    onDrop={(e) => {
                      onDropZoneDrop(e, idx + 1);
                    }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function DropZone({
  active,
  dragging,
  empty,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  index: number;
  active: boolean;
  dragging: boolean;
  empty?: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const handleDragLeave = (e: React.DragEvent) => {
    const related = e.relatedTarget;
    if (related instanceof Node && e.currentTarget.contains(related)) return;
    onDragLeave();
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={handleDragLeave}
      onDrop={onDrop}
      className={cn(
        'fm-drop-zone flex items-center justify-center',
        empty && 'fm-drop-zone-empty',
        empty && active && 'fm-drop-zone-active',
        !empty && dragging && 'fm-drop-zone-slot',
        !empty && active && 'fm-drop-zone-active',
        !empty && !dragging && 'fm-drop-zone-rest',
      )}
    >
      {empty ? (
        <div className="flex flex-col items-center px-6 text-center">
          <div className="mb-3 inline-flex size-11 items-center justify-center rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-3)]">
            <Icon kind="plus" size={20} stroke={2} />
          </div>
          <p className="text-[14px] font-medium text-[var(--ink-1)]">Add your first block</p>
          <p className="mt-1 max-w-[280px] text-[12.5px] leading-relaxed text-[var(--ink-3)]">
            Drag a block from the library into this area, or click a block to add it here.
          </p>
        </div>
      ) : active ? (
        <div className="fm-drop-line">
          <span className="fm-drop-label">Drop here</span>
        </div>
      ) : dragging ? (
        <div className="fm-drop-slot-hint" aria-hidden />
      ) : null}
    </div>
  );
}
